import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Printer as PrinterIcon, Wifi, WifiOff, TestTubes, Trash2,
  Palette, Contrast, Image as ImageIcon, Save, MapPin, RefreshCw, Info,
  AlertTriangle, MonitorSmartphone, CheckCircle2,
} from "lucide-react";

// Mapping keys shown as three lanes — the retailer picks one of their
// DETECTED Windows printers for each. B&W → Color → Photo. Photo lane
// covers passport + glossy prints.
const MAP_KINDS = [
  { key: "bw",    label: "Black & White",     hint: "Regular B&W documents",              icon: Contrast,  color: "text-slate-600 bg-slate-500/10" },
  { key: "color", label: "Color",             hint: "Color documents & photo prints",     icon: Palette,   color: "text-fuchsia-600 bg-fuchsia-500/10" },
  { key: "photo", label: "Photo / Passport",  hint: "Glossy 4×6\" passport & photo prints", icon: ImageIcon, color: "text-brand-green bg-brand-green/10" },
];

const relTime = (iso) => {
  if (!iso) return "never";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
};

export default function Printers() {
  const [pcs, setPcs] = useState([]);
  const [activePcId, setActivePcId] = useState(null);
  const [selectedPcId, setSelectedPcId] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [pmap, setPmap] = useState({ bw: null, color: null, photo: null });
  const [pmapSaving, setPmapSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [settingActive, setSettingActive] = useState(false);
  const [heldOrders, setHeldOrders] = useState([]);

  const loadPcs = useCallback(async () => {
    try {
      const { data } = await api.get("/pcs");
      const list = data.pcs || [];
      setPcs(list);
      setActivePcId(data.active_pc_id || null);
      // Auto-select active or first PC if nothing chosen yet.
      setSelectedPcId((prev) => prev || data.active_pc_id || (list[0]?.pc_id ?? null));
    } catch (e) {
      // Non-blocking
    }
  }, []);

  const loadPrinters = useCallback(async (pcId) => {
    if (!pcId) { setPrinters([]); return; }
    const { data } = await api.get("/printers", { params: { pc_id: pcId } });
    setPrinters(data);
  }, []);

  const loadMap = useCallback(async (pcId) => {
    if (!pcId) { setPmap({ bw: null, color: null, photo: null }); return; }
    try {
      const { data } = await api.get("/printers/map", { params: { pc_id: pcId } });
      setPmap({ bw: data.bw || null, color: data.color || null, photo: data.photo || null });
    } catch { /* non-blocking */ }
  }, []);

  const loadHeld = useCallback(async () => {
    try {
      const { data } = await api.get("/orders/awaiting-printer-setup");
      setHeldOrders(Array.isArray(data) ? data : []);
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { loadPcs(); loadHeld(); }, [loadPcs, loadHeld]);

  useEffect(() => {
    if (selectedPcId) {
      loadPrinters(selectedPcId);
      loadMap(selectedPcId);
    }
  }, [selectedPcId, loadPrinters, loadMap]);

  const refresh = async () => {
    if (!selectedPcId) {
      toast.error("Install the Windows Connector on this PC first.");
      return;
    }
    setRefreshing(true);
    try {
      const { data } = await api.post("/printers/refresh", null, { params: { pc_id: selectedPcId } });
      setPrinters(data);
      await loadMap(selectedPcId);
      await loadPcs();
      toast.success(
        data.length
          ? `Found ${data.length} printer${data.length === 1 ? "" : "s"} on this PC.`
          : "No printers detected yet — is the Windows Connector running on this PC?",
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from your printer list?`)) return;
    try {
      await api.delete(`/printers/${p.id}`);
      toast.success(`Removed ${p.name}`);
      await loadPrinters(selectedPcId);
      await loadMap(selectedPcId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const testPrint = async (p) => {
    try {
      await api.post(`/printers/${p.id}/test`);
      toast.success(`Test page queued to "${p.name}"`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveMap = async () => {
    if (!selectedPcId) return;
    setPmapSaving(true);
    try {
      const { data } = await api.put("/printers/map", pmap, { params: { pc_id: selectedPcId } });
      setPmap({ bw: data.bw || null, color: data.color || null, photo: data.photo || null });
      setActivePcId(data.pc_id || selectedPcId);
      const released = data.released_orders || 0;
      if (released > 0) {
        toast.success(`Mapping saved · ${released} held order${released === 1 ? "" : "s"} released to print.`);
      } else {
        toast.success("Printer mapping saved. Every print will route to the correct printer on this PC.");
      }
      await loadHeld();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPmapSaving(false);
    }
  };

  const setActive = async () => {
    if (!selectedPcId || selectedPcId === activePcId) return;
    setSettingActive(true);
    try {
      const { data } = await api.post("/pcs/active", { pc_id: selectedPcId });
      setActivePcId(data.active_pc_id);
      toast.success("This PC is now the primary — walk-in prints will route here.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSettingActive(false);
    }
  };

  const currentPc = pcs.find((p) => p.pc_id === selectedPcId);
  const isActive = selectedPcId === activePcId;

  return (
    <div className="space-y-6" data-testid="printers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Devices</p>
          <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">Printers</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Printers are auto-detected per-PC by the Windows Connector — install it on every PC you print from.
          </p>
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing || !selectedPcId}
          className="bg-primary hover:bg-brand-blue-hover text-white"
          data-testid="printer-refresh-btn"
        >
          <RefreshCw size={15} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh from Windows"}
        </Button>
      </div>

      {/* PC selector — one entry per PC the user has connected. */}
      <Card className="p-5 border-border bg-card" data-testid="pc-selector-card">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <MonitorSmartphone size={18} />
          </div>
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm font-medium">Which PC do you want to manage?</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Each PC keeps its own printer list &amp; lane mapping. Log in from a different PC and its printers appear here automatically.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={selectedPcId || "__none__"} onValueChange={(v) => setSelectedPcId(v === "__none__" ? null : v)}>
              <SelectTrigger className="w-[240px]" data-testid="pc-selector-select">
                <SelectValue placeholder="No PC connected yet" />
              </SelectTrigger>
              <SelectContent>
                {pcs.length === 0 ? (
                  <SelectItem value="__none__" disabled>No PC connected yet</SelectItem>
                ) : pcs.map((p) => (
                  <SelectItem key={p.pc_id} value={p.pc_id} data-testid={`pc-selector-option-${p.pc_id}`}>
                    <span className="inline-flex items-center gap-2">
                      {p.pc_id === activePcId && <CheckCircle2 size={13} className="text-brand-green" />}
                      <span>{p.host || p.pc_id}</span>
                      <span className="text-[10px] text-muted-foreground">· {p.printer_count} printer{p.printer_count === 1 ? "" : "s"}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant={isActive ? "outline" : "default"}
              onClick={setActive}
              disabled={settingActive || isActive || !selectedPcId}
              data-testid="pc-set-active-btn"
              className={isActive ? "" : "bg-brand-green hover:bg-brand-green-hover text-white"}
            >
              {isActive ? (<><CheckCircle2 size={14} className="mr-1.5" /> Primary PC</>) : "Set as Primary"}
            </Button>
          </div>
        </div>
        {currentPc && (
          <div className="mt-3 pl-13 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] text-muted-foreground">
            <div><span className="uppercase tracking-wider">Host</span><br/><span className="text-foreground">{currentPc.host || currentPc.pc_id}</span></div>
            <div><span className="uppercase tracking-wider">Printers</span><br/><span className="text-foreground">{currentPc.printer_count}</span></div>
            <div><span className="uppercase tracking-wider">OS</span><br/><span className="text-foreground">{currentPc.os || "—"}</span></div>
            <div><span className="uppercase tracking-wider">Last seen</span><br/><span className="text-foreground">{relTime(currentPc.last_seen)}</span></div>
          </div>
        )}
      </Card>

      {/* Explainer strip — makes the "no manual add" model obvious. */}
      <Card className="p-4 border-brand-blue/30 bg-brand-blue/5 flex items-start gap-3" data-testid="printer-detection-help">
        <Info size={15} className="text-brand-blue mt-0.5 flex-shrink-0" />
        <div className="text-xs text-muted-foreground leading-relaxed">
          Don&apos;t see your printer? <b>Turn it on</b>, make sure it&apos;s added to <b>Windows &nbsp;→&nbsp; Printers &amp; Scanners</b>, then click <b>Refresh from Windows</b>. Virtual printers (Print&nbsp;to&nbsp;PDF, XPS, OneNote, Fax) are auto-filtered.
        </div>
      </Card>

      {/* STRICT routing hold banner — orders paused because required lane isn't mapped. */}
      {heldOrders.length > 0 && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5 flex items-start gap-3" data-testid="printers-held-banner">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm leading-relaxed">
            <p className="font-medium text-amber-700 dark:text-amber-500">
              {heldOrders.length} print{heldOrders.length === 1 ? " is" : "s are"} paused — printer mapping incomplete
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Prints stay strictly on the correct lane (B&amp;W → B&amp;W printer, Color → Color printer, Photo → Photo printer) — no cross-routing. Pick a printer for every lane you use below and click <b>Save mapping</b> to release them.
            </p>
          </div>
        </Card>
      )}

      {printers.length === 0 ? (
        <Card className="p-12 border-border bg-card text-center" data-testid="printers-empty">
          <div className="h-16 w-16 mx-auto rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <PrinterIcon size={24} />
          </div>
          <p className="mt-4 font-display text-lg">
            {pcs.length === 0 ? "No PC connected yet" : "No printers detected on this PC yet"}
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Install the <b>Windows Connector</b> on this shop PC and turn on your printer. Its exact Windows name will appear here within 30&nbsp;seconds.
          </p>
        </Card>
      ) : (
        <React.Fragment>
          {/* Printer mapping — retailer picks which detected printer handles B&W / Color / Photo — per PC */}
          <Card className="p-6 border-border bg-card" data-testid="printer-map-card">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="flex items-center gap-2">
                <MapPin size={18} className="text-primary" />
                <div>
                  <h3 className="font-display font-semibold text-lg leading-tight">
                    Printer mapping <span className="text-xs font-normal text-muted-foreground">· {currentPc?.host || selectedPcId}</span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Save once — every future print job on this PC routes to the mapped hardware. Windows default is <b>never</b> used.
                  </p>
                </div>
              </div>
              <Button
                onClick={saveMap}
                disabled={pmapSaving}
                className="bg-brand-green hover:bg-brand-green-hover text-white"
                data-testid="printer-map-save-btn"
              >
                <Save size={14} className="mr-2" /> {pmapSaving ? "Saving…" : "Save mapping"}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MAP_KINDS.map((k) => {
                const Icon = k.icon;
                return (
                  <div key={k.key} className="rounded-lg border border-border p-4 bg-secondary/30" data-testid={`printer-map-${k.key}-card`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${k.color}`}>
                        <Icon size={15} />
                      </div>
                      <div>
                        <p className="text-sm font-medium leading-tight">{k.label}</p>
                        <p className="text-[11px] text-muted-foreground leading-tight">{k.hint}</p>
                      </div>
                    </div>
                    <Select
                      value={pmap[k.key] || "__none__"}
                      onValueChange={(v) => setPmap((p) => ({ ...p, [k.key]: v === "__none__" ? null : v }))}
                    >
                      <SelectTrigger className="w-full" data-testid={`printer-map-${k.key}-select`}>
                        <SelectValue placeholder="Pick a printer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {printers.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name} {p.status === "online" ? "· online" : "· offline"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Detected printers cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {printers.map((p) => (
              <Card key={p.id} className="p-6 border-border bg-card hover:shadow-sm transition-all hover:-translate-y-[2px]" data-testid={`printer-card-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0 ${p.status === "online" ? "bg-brand-green/10 text-brand-green" : "bg-muted text-muted-foreground"}`}>
                      <PrinterIcon size={20} />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate" title={p.name}>{p.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {p.brand || "Auto-detected"}{p.model ? ` · ${p.model}` : ""}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <Badge variant="outline" className={p.status === "online" ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "bg-muted text-muted-foreground"}>
                    {p.status === "online" ? <Wifi size={12} className="mr-1" /> : <WifiOff size={12} className="mr-1" />}
                    {p.status}
                  </Badge>
                  {p.last_seen && (
                    <span className="text-[11px] text-muted-foreground">
                      seen {relTime(p.last_seen)}
                    </span>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => testPrint(p)} data-testid={`printer-test-${p.id}`}>
                    <TestTubes size={14} className="mr-1" /> Test print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-destructive hover:text-destructive" data-testid={`printer-delete-${p.id}`}>
                    <Trash2 size={14} className="mr-1" /> Remove
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </React.Fragment>
      )}
    </div>
  );
}
